import React from 'react';

import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

export type VariantKey =
  | 'BodySmallLight'
  | 'BodySmallDark'
  | 'BodySmallDarker'
  | 'BodySmallMedium'
  | 'BodySmallMediumDark'
  | 'BodySmallMediumDarker'
  | 'BodySmallSemiBold'
  | 'BodySmallSemiBoldDark'
  | 'BodySmallSemiBoldDarker'
  | 'BodySmallBold'
  | 'BodySmallBoldDark'
  | 'BodySmallBoldDarker'
  | 'BodyMediumLight'
  | 'BodyMediumDark'
  | 'BodyMediumDarker'
  | 'BodyMediumMedium'
  | 'BodyMediumMediumDark'
  | 'BodyMediumMediumDarker'
  | 'BodyMediumSemiBold'
  | 'BodyMediumSemiBoldDark'
  | 'BodyMediumSemiBoldDarker'
  | 'BodyMediumBold'
  | 'BodyMediumBoldDark'
  | 'BodyMediumBoldDarker'
  | 'BodyLargeLight'
  | 'BodyLargeDark'
  | 'BodyLargeDarker'
  | 'BodyLargeMedium'
  | 'BodyLargeMediumDark'
  | 'BodyLargeMediumDarker'
  | 'BodyLargeSemiBold'
  | 'BodyLargeSemiBoldDark'
  | 'BodyLargeSemiBoldDarker'
  | 'BodyLargeBold'
  | 'BodyLargeBoldDark'
  | 'BodyLargeBoldDarker'
  | 'HeadingLight'
  | 'HeadingDark'
  | 'HeadingDarker'
  | 'HeadingMedium'
  | 'HeadingMediumDark'
  | 'HeadingMediumDarker'
  | 'HeadingSemiBold'
  | 'HeadingSemiBoldDark'
  | 'HeadingSemiBoldDarker'
  | 'HeadingBold'
  | 'HeadingBoldDark'
  | 'HeadingBoldDarker';

interface CustomTypographyProps {
  size?: '10' | '12' | '13' | '14' | '16' | '18' | '20' | '22' | '32';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  color?: 'grey300' | 'grey400' | 'grey500' | 'grey600' | 'grey700' | 'grey800' | 'grey900' | 'errorText' | 'hyperText' | 'primary' | 'blue700' | 'white' | 'red' | 'hyperFont' | 'grey950' | 'inherit';
  variants?: VariantKey;
  component?: React.ElementType;
  children?: React.ReactNode;
}

const fontSizeMap: { [key: string]: string } = {
  '10': '10px',
  '12': '12px',
  '13': '13px',
  '14': '14px',
  '16': '16px',
  '18': '18px',
  '20': '20px',
  '22': '22px',
  '32': '32px',
  _: '14px' // Default size
};

const fontWeightMap: { [key: string]: number } = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  _: 400 // Default weight
};

const fontColorMap: { [key: string]: string } = {
  grey300: '#d1d1d1',
  grey400: '#b5b5b5',
  grey500: '#999999',
  grey600: '#7A7A7A',
  grey700: '#5c5c5c',
  grey800: '#3D3D3D',
  grey900: '#1F1F1F',
  errorText: '#d9302a',
  hyperText: '#5d9cec',
  hyperFont: '#2e93ff',
  blue700: '#0361a0',
  white: 'rgb(255 255 255)',
  red: '#e4706d',
  grey950: '#202129',
  inherit: 'inherit',
  _: '#3D3D3D' // Default color
};

const fontVariantMap: { [key in VariantKey]: Omit<CustomTypographyProps, 'variants'> } = {
  BodySmallLight: { size: '12', weight: 'normal', color: 'grey600' },
  BodySmallDark: { size: '12', weight: 'normal', color: 'grey800' },
  BodySmallDarker: { size: '12', weight: 'normal', color: 'grey900' },
  BodySmallMedium: { size: '12', weight: 'medium', color: 'grey600' },
  BodySmallMediumDark: { size: '12', weight: 'medium', color: 'grey800' },
  BodySmallMediumDarker: { size: '12', weight: 'medium', color: 'grey900' },
  BodySmallSemiBold: { size: '12', weight: 'semibold', color: 'grey600' },
  BodySmallSemiBoldDark: { size: '12', weight: 'semibold', color: 'grey800' },
  BodySmallSemiBoldDarker: { size: '12', weight: 'semibold', color: 'grey900' },
  BodySmallBold: { size: '12', weight: 'bold', color: 'grey600' },
  BodySmallBoldDark: { size: '12', weight: 'bold', color: 'grey800' },
  BodySmallBoldDarker: { size: '12', weight: 'bold', color: 'grey900' },
  BodyMediumLight: { size: '14', weight: 'normal', color: 'grey600' },
  BodyMediumDark: { size: '14', weight: 'normal', color: 'grey800' },
  BodyMediumDarker: { size: '14', weight: 'normal', color: 'grey900' },
  BodyMediumMedium: { size: '14', weight: 'medium', color: 'grey600' },
  BodyMediumMediumDark: { size: '14', weight: 'medium', color: 'grey800' },
  BodyMediumMediumDarker: { size: '14', weight: 'medium', color: 'grey900' },
  BodyMediumSemiBold: { size: '14', weight: 'semibold', color: 'grey600' },
  BodyMediumSemiBoldDark: { size: '14', weight: 'semibold', color: 'grey800' },
  BodyMediumSemiBoldDarker: { size: '14', weight: 'semibold', color: 'grey900' },
  BodyMediumBold: { size: '14', weight: 'bold', color: 'grey600' },
  BodyMediumBoldDark: { size: '14', weight: 'bold', color: 'grey800' },
  BodyMediumBoldDarker: { size: '14', weight: 'bold', color: 'grey900' },
  BodyLargeLight: { size: '16', weight: 'normal', color: 'grey600' },
  BodyLargeDark: { size: '16', weight: 'normal', color: 'grey800' },
  BodyLargeDarker: { size: '16', weight: 'normal', color: 'grey900' },
  BodyLargeMedium: { size: '16', weight: 'medium', color: 'grey600' },
  BodyLargeMediumDark: { size: '16', weight: 'medium', color: 'grey800' },
  BodyLargeMediumDarker: { size: '16', weight: 'medium', color: 'grey900' },
  BodyLargeSemiBold: { size: '16', weight: 'semibold', color: 'grey600' },
  BodyLargeSemiBoldDark: { size: '16', weight: 'semibold', color: 'grey800' },
  BodyLargeSemiBoldDarker: { size: '16', weight: 'semibold', color: 'grey900' },
  BodyLargeBold: { size: '16', weight: 'bold', color: 'grey600' },
  BodyLargeBoldDark: { size: '16', weight: 'bold', color: 'grey800' },
  BodyLargeBoldDarker: { size: '16', weight: 'bold', color: 'grey900' },
  HeadingLight: { size: '18', weight: 'normal', color: 'grey600' },
  HeadingDark: { size: '18', weight: 'normal', color: 'grey800' },
  HeadingDarker: { size: '18', weight: 'normal', color: 'grey900' },
  HeadingMedium: { size: '18', weight: 'medium', color: 'grey600' },
  HeadingMediumDark: { size: '18', weight: 'medium', color: 'grey800' },
  HeadingMediumDarker: { size: '18', weight: 'medium', color: 'grey900' },
  HeadingSemiBold: { size: '18', weight: 'semibold', color: 'grey600' },
  HeadingSemiBoldDark: { size: '18', weight: 'semibold', color: 'grey800' },
  HeadingSemiBoldDarker: { size: '18', weight: 'semibold', color: 'grey900' },
  HeadingBold: { size: '18', weight: 'bold', color: 'grey600' },
  HeadingBoldDark: { size: '18', weight: 'bold', color: 'grey800' },
  HeadingBoldDarker: { size: '18', weight: 'bold', color: 'grey900' }
};

const CustomTypography = styled(Typography, {
  shouldForwardProp: prop => !['weight', 'size', 'color', 'variants'].includes(prop as string)
})<CustomTypographyProps>(({ size, weight, color, variants, theme }) => {
  let fontSize = fontSizeMap['_'];
  let fontWeight = fontWeightMap['_'];
  let fontColor = fontColorMap['_'];
  if (variants && fontVariantMap[variants]) {
    const { size: variantSize, weight: variantWeight, color: variantColor } = fontVariantMap[variants];
    fontSize = fontSizeMap[variantSize as keyof typeof fontSizeMap] || fontSizeMap['_'];
    fontWeight = fontWeightMap[variantWeight as keyof typeof fontWeightMap] || fontWeightMap['_'];
    fontColor = fontColorMap[variantColor as keyof typeof fontColorMap] || fontColorMap['_'];
  } else {
    fontSize = fontSizeMap[size || '_'];
    fontWeight = fontWeightMap[weight || '_'];
    fontColor = color === 'primary' ? theme.palette.primary.main : fontColorMap[color || '_'];
  }
  return {
    fontSize: `${fontSize} !important`,
    fontWeight: `${fontWeight} !important`,
    color: `${fontColor} !important`
  };
});

export default CustomTypography;
